import React, { useEffect, useRef, useState, useCallback } from 'react';
import clsx from 'clsx';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ProgressSpinner } from 'primereact/progressspinner';
import i18next from 'i18next';
import usePaginationStore from '../store/usePaginationStore';
import { debounce } from 'lodash';
import { t } from 'i18next';

const PaginationNavBar = ({
    isCollapsed = false,
    notification = false,
}) => {
    const {
        currentPage,
        totalItems,
        limitFilter,
        paginationLoading,
        PaginationNavBarQuery,
        setPage,
        setLimitFilter,
        setPaginationNavBarQuery,
    } = usePaginationStore();

    const [isActive, setIsActive] = useState(false);
    const searchPanelRef = useRef(null);
    const dropdownRef = useRef(null);

    const [searchTerm, setSearchTerm] = useState(PaginationNavBarQuery);

    const totalPages = Math.ceil(totalItems / limitFilter);

    const handlePrevClick = () => {
        if (currentPage > 1) {
            setPage(currentPage - 1);
        }
    };

    const handleNextClick = () => {
        if (currentPage < totalPages) {
            setPage(currentPage + 1);
        }
    };

    const handleLimitChange = (limit) => {
        setLimitFilter(limit);
    };

    const debouncedSetQuery = useCallback(
        debounce((query) => {
            setPaginationNavBarQuery(query);
        }, 500),
        []
    );

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
        debouncedSetQuery(value);
    };

    const limitOptions = [
        { label: '10', value: 10 },
        { label: '20', value: 20 },
        { label: '30', value: 30 },
        { label: '40', value: 40 },
        { label: '50', value: 50 }
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsActive(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        setSearchTerm(PaginationNavBarQuery);
    }, [PaginationNavBarQuery]);

    return (
        <div className={clsx('pagination-navbar', { 'collapsed': isCollapsed, 'notification': notification })}>
            <div className="navbar-info">
                {paginationLoading ? (
                    <ProgressSpinner style={{ width: '25px', height: '25px' }} strokeWidth="4" />
                ) : (
                    <span className='is-size-6 has-text-grey'>{`${currentPage}/${totalPages}`}</span>
                )}
            </div>

            <div className="p-2">
                <button
                    className="button is-light radius-20 px-4 mr-1"
                    onClick={handlePrevClick}
                    disabled={paginationLoading || currentPage === 1}
                >
                    <span className="icon is-small">
                        <i className="fas fa-arrow-left"></i>
                    </span>
                </button>

                <button
                    className="button is-light radius-20 px-4 mr-1"
                    onClick={handleNextClick}
                    disabled={paginationLoading || currentPage === totalPages}
                >
                    <span className="icon is-small">
                        <i className="fas fa-arrow-right"></i>
                    </span>
                </button>
            </div>

            <div className="search-bar is-hidden-touch">
                <div className="field is-fullwidth">
                    <p className="control has-icons-left">
                        <input
                            className="input"
                            type='text'
                            placeholder={i18next.t('userDashboard.pagination-navbar.inputPlaceholder')}
                            value={searchTerm}
                            onChange={handleSearchChange}
                            disabled={paginationLoading}
                        />
                        <span className="icon is-small is-left">
                            <i className="fa-solid fa-magnifying-glass"></i>
                        </span>
                    </p>
                </div>
            </div>

            <div className="search-icon is-hidden-desktop">
                <a className="button" onClick={(e) => searchPanelRef.current.toggle(e)} disabled={paginationLoading}>
                    <i className="fa-solid fa-magnifying-glass"></i>
                </a>

                <OverlayPanel ref={searchPanelRef}>
                    <div className="field is-fullwidth">
                        <p className="control has-icons-left">
                            <input
                                className="input"
                                type='text'
                                placeholder={i18next.t('userDashboard.pagination-navbar.inputPlaceholder')}
                                value={searchTerm}
                                onChange={handleSearchChange}
                                disabled={paginationLoading}
                            />
                            <span className="icon is-small is-left">
                                <i className="fa-solid fa-magnifying-glass"></i>
                            </span>
                        </p>
                    </div>
                </OverlayPanel>
            </div>

            <div className={clsx('dropdown mr-3', { 'is-active': isActive })} ref={dropdownRef}>
                <div className="dropdown-trigger">
                    <button
                        className="button is-outlined radius-8"
                        aria-haspopup="true"
                        aria-controls="dropdown-menu"
                        onClick={() => setIsActive(!isActive)}
                        disabled={paginationLoading}
                    >
                        <span>{limitFilter || 'Select Limit'} {limitFilter ? i18next.t('userDashboard.pagination-navbar.results') : ''}</span>
                        <span className="icon is-small">
                            <i className="fas fa-angle-down" aria-hidden="true"></i>
                        </span>
                    </button>
                </div>

                <div className="dropdown-menu" id="dropdown-menu" role="menu">
                    <div className="dropdown-content">
                        {limitOptions.map((option) => (
                            <a
                                key={option.value}
                                className="dropdown-item"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (!paginationLoading) {
                                        handleLimitChange(option.value);
                                        setIsActive(false);
                                    }
                                }}
                            >
                                {option.label} {i18next.t('userDashboard.pagination-navbar.results')}
                            </a>
                        ))}
                    </div>
                </div>
            </div>

            <button className="button is-outlined radius-8" disabled={true}>
                <span className="icon pl-1">
                    <i className="fa-solid fa-download"></i>
                </span>
                <span>{i18next.t('userDashboard.pagination-navbar.export')}</span>
            </button>
        </div>
    );
};

export default PaginationNavBar;